import { useEffect, useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";

import { Button } from "@/components/ui/Button";
import { Input, Label } from "@/components/ui/Input";
import { apiFetch } from "@/lib/api";

type Environment = "sandbox" | "production";

interface Candidate {
  approvedComponentId: number;
  manufacturerPartNumber: string;
  digiKeyPartNumber: string | null;
  manufacturer: string | null;
}

interface PreviewLine {
  purchaseOrderItemId: number;
  sku: string;
  description: string | null;
  quantity: number;
  unitPrice: number | null;
  candidates: Candidate[];
}

interface Preview {
  purchaseOrderId: number;
  purchaseOrderNumber: string;
  alreadyOrdered: boolean;
  lines: PreviewLine[];
  ordering: Record<
    Environment,
    {
      clientConfigured: boolean;
      connected: boolean;
      expiresAt: string | null;
      productionEnabled: boolean;
    }
  >;
}

export function DigiKeyOrderModal(props: {
  purchaseOrderId: number;
  onClose: () => void;
  onOrdered: () => void;
}): JSX.Element {
  const [preview, setPreview] = useState<Preview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [environment, setEnvironment] = useState<Environment>("sandbox");
  const [selectedParts, setSelectedParts] = useState<Record<number, string>>({});
  const [productionConfirmed, setProductionConfirmed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void apiFetch<Preview>(
      `/api/mrp/purchase-orders/${props.purchaseOrderId}/digikey-order-preview`,
    )
      .then((loaded) => {
        if (cancelled) return;
        setPreview(loaded);
        const initial: Record<number, string> = {};
        for (const line of loaded.lines) {
          const available = line.candidates.filter(
            (candidate) => candidate.digiKeyPartNumber != null,
          );
          if (available.length === 1) {
            initial[line.purchaseOrderItemId] = available[0]!.digiKeyPartNumber!;
          }
        }
        setSelectedParts(initial);
      })
      .catch((error: Error) => {
        if (!cancelled) setPreviewError(error.message);
      });
    return () => {
      cancelled = true;
    };
  }, [props.purchaseOrderId]);

  const selectedLines = useMemo(() => {
    if (preview == null) return [];
    return preview.lines
      .map((line) => {
        const digiKeyPartNumber = selectedParts[line.purchaseOrderItemId]?.trim() ?? "";
        const candidate = line.candidates.find(
          (row) => row.digiKeyPartNumber === digiKeyPartNumber,
        );
        return {
          customerLineItemId: String(line.purchaseOrderItemId),
          digiKeyPartNumber,
          manufacturerPartNumber: candidate?.manufacturerPartNumber ?? null,
          productDescription: line.description,
          requestedQuantity: line.quantity,
          unitPrice: line.unitPrice,
        };
      });
  }, [preview, selectedParts]);

  const allLinesResolved =
    preview != null &&
    preview.lines.length > 0 &&
    selectedLines.every((line) => line.digiKeyPartNumber !== "");
  const connection = preview?.ordering[environment];
  const submitMutation = useMutation({
    mutationFn: () =>
      apiFetch<{
        accepted: boolean;
        salesOrderId: number | null;
        lifecycleError?: string;
      }>(`/api/mrp/purchase-orders/${props.purchaseOrderId}/digikey-order`, {
        method: "POST",
        body: {
          environment,
          productionCoordinationConfirmed: productionConfirmed,
          lines: selectedLines,
        },
      }),
    onSuccess: () => {
      props.onOrdered();
    },
  });

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
      onClick={(event) => {
        if (event.target === event.currentTarget && !submitMutation.isPending) props.onClose();
      }}
    >
      <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-lg bg-white p-5 shadow-xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Order from DigiKey</h2>
            <p className="mt-1 text-sm text-slate-600">
              Buyer contact is fixed to e.edwards@smartgridsolutions.com, shipping customer
              10274286, and billing account 4276136.
            </p>
          </div>
          <Button type="button" variant="secondary" onClick={props.onClose}>
            Close
          </Button>
        </div>

        {previewError ? <p className="mt-4 text-sm text-red-700">{previewError}</p> : null}
        {preview == null && previewError == null ? (
          <p className="mt-4 text-sm text-slate-600">Loading DigiKey part choices...</p>
        ) : null}

        {preview ? (
          <div className="mt-4 space-y-4">
            <div className="space-y-1">
              <Label htmlFor="digikey-order-environment">Environment</Label>
              <select
                id="digikey-order-environment"
                className="h-10 rounded border border-slate-300 bg-white px-3 text-sm"
                value={environment}
                onChange={(event) => {
                  setEnvironment(event.target.value as Environment);
                  setProductionConfirmed(false);
                }}
              >
                <option value="sandbox">Sandbox, canned response only</option>
                <option value="production">Production, can fulfill and invoice</option>
              </select>
              {!connection?.clientConfigured || !connection.connected ? (
                <p className="text-sm text-amber-700">
                  This environment must be configured and connected on Admin, Distributor APIs.
                </p>
              ) : null}
            </div>

            <div className="space-y-3">
              {preview.lines.map((line) => (
                <div key={line.purchaseOrderItemId} className="rounded border border-slate-200 p-3">
                  <div className="flex flex-wrap justify-between gap-2">
                    <span className="font-mono font-semibold">{line.sku}</span>
                    <span className="text-sm text-slate-600">
                      Quantity {line.quantity.toLocaleString()}
                    </span>
                  </div>
                  <Label htmlFor={`digikey-part-${line.purchaseOrderItemId}`}>
                    DigiKey part number
                  </Label>
                  {line.candidates.some((candidate) => candidate.digiKeyPartNumber != null) ? (
                    <select
                      id={`digikey-part-${line.purchaseOrderItemId}`}
                      className="mt-1 h-10 w-full rounded border border-slate-300 bg-white px-3 text-sm"
                      value={selectedParts[line.purchaseOrderItemId] ?? ""}
                      onChange={(event) =>
                        setSelectedParts((current) => ({
                          ...current,
                          [line.purchaseOrderItemId]: event.target.value,
                        }))
                      }
                    >
                      <option value="">Select a matched approved component</option>
                      {line.candidates
                        .filter((candidate) => candidate.digiKeyPartNumber != null)
                        .map((candidate) => (
                          <option
                            key={candidate.approvedComponentId}
                            value={candidate.digiKeyPartNumber!}
                          >
                            {candidate.digiKeyPartNumber} - {candidate.manufacturerPartNumber}
                          </option>
                        ))}
                    </select>
                  ) : (
                    <Input
                      id={`digikey-part-${line.purchaseOrderItemId}`}
                      value={selectedParts[line.purchaseOrderItemId] ?? ""}
                      onChange={(event) =>
                        setSelectedParts((current) => ({
                          ...current,
                          [line.purchaseOrderItemId]: event.target.value,
                        }))
                      }
                      placeholder="Enter the DigiKey part number"
                    />
                  )}
                </div>
              ))}
            </div>

            {environment === "production" ? (
              <label className="flex items-start gap-2 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-900">
                <input
                  type="checkbox"
                  checked={productionConfirmed}
                  onChange={(event) => setProductionConfirmed(event.target.checked)}
                />
                DigiKey is actively coordinating this production test order. I understand it can
                fulfill and invoice normally.
              </label>
            ) : null}

            {submitMutation.error ? (
              <p className="text-sm text-red-700">{(submitMutation.error as Error).message}</p>
            ) : null}

            {submitMutation.data?.accepted ? (
              <div className="rounded border border-green-200 bg-green-50 p-3 text-sm text-green-900">
                <p className="font-semibold">DigiKey accepted the order.</p>
                <p>
                  Sales order:{" "}
                  {submitMutation.data.salesOrderId === -1
                    ? "received, identifier pending"
                    : submitMutation.data.salesOrderId ?? "not returned"}
                </p>
                {submitMutation.data.lifecycleError ? (
                  <p className="mt-1 text-amber-800">
                    DigiKey accepted it, but the Hub could not mark the purchase order ordered:{" "}
                    {submitMutation.data.lifecycleError}
                  </p>
                ) : null}
                <Button type="button" className="mt-2" onClick={props.onClose}>
                  Done
                </Button>
              </div>
            ) : (
              <Button
                type="button"
                isLoading={submitMutation.isPending}
                disabled={
                  !allLinesResolved ||
                  !connection?.clientConfigured ||
                  !connection.connected ||
                  !connection.productionEnabled ||
                  (environment === "production" && !productionConfirmed)
                }
                onClick={() => submitMutation.mutate()}
              >
                {environment === "sandbox" ? "Submit sandbox order" : "Submit production order"}
              </Button>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}
